import React, { useEffect, useState, useRef, useCallback } from 'react'

import jwtDecode from 'jwt-decode'
import * as firebaseui from 'firebaseui'
import { useSelector, shallowEqual, useDispatch } from 'react-redux'
import { useHistory, useLocation } from 'react-router-dom'
import { Alert, Box, Button, Card, useTheme, Typography } from '@mui/material'
import { auth, handleFirebaseLogout } from 'providers/firebaseAuth'
import { useSearchParams } from 'react-router-dom-v5-compat'

import { get } from 'utils/lodash'
import { handleError } from 'utils/error'
import { setSelectedLoginDetails } from 'slices/login/LoginPersistSlice'
import { useLazyGetBusinessInfoQuery } from 'api/business/getBusinessInfo'
import { useLazyGetAccountStatusQuery } from 'api/account/getAccountStatus'
import { useRegisterUserMutation } from 'api/account/registerUser'
import { useUpdateGCPUserMutation } from 'api/account/updateGCPUser'
import { getFirstNameAndLastName } from 'utils/parseName'
import { isSSOProvider } from 'settings/constants/auth'

import T from 'T'
import Constants from 'Constants'
import routes from 'router/routes'
import Loader from 'components/common/loader'
import { signInOptions } from './settings'
import { useMutationObserver } from './useMutationObserver'
import RecoverPassword from './RecoverPassword'
import 'firebaseui/dist/firebaseui.css'
import './style.scss'
import FacebookLogin from './FacebookLogin'

const { PORTAL_AUTH_TENANT_ID } = Constants

const SSOLogin = ({ onBack, accountVerified = false, goToCreate }) => {
  const theme = useTheme()
  const history = useHistory()
  const location = useLocation()
  const dispatch = useDispatch()
  const [searchParams] = useSearchParams()
  const { pathname } = location
  const [getBusinessInfo, { isFetching: isLoadingGetBusinessInfo, data: businessInfo, isSuccess: isGetBusinessInfoSuccess }] =
    useLazyGetBusinessInfoQuery()
  const [getAccountStatus, { data: isActiveAccount, isSuccess: isGetAccountStatusSuccess }] = useLazyGetAccountStatusQuery()
  const [registerUser, { isLoading: isLoadingRegisterUser }] = useRegisterUserMutation()
  const [updateGCPUser, { isLoading: isLoadingUpdateUser }] = useUpdateGCPUserMutation()
  const [showRecoverPasswordBtn, setShowRecoverPasswordBtn] = useState(false)
  const [recoverPasswordScreen, setRecoverPasswordScreen] = useState({ isOpen: false, email: '' })
  const [passwordLinkingEmail, setPasswordLinkingEmail] = useState('')
  const [hasAccountIdError, setHasAccountIdError] = useState(false)
  const [hasAccountDeactivatedError, setHasAccountDeactivatedError] = useState(false)

  const urlBusinessName = searchParams.get('business_name') || searchParams.get('bid')
  const oldBusinessName = useSelector(state => get(state, 'LoginPersist.selectedLoginDetails.businessName', ''), shallowEqual)
  const currentBusinessName = urlBusinessName || oldBusinessName

  const businessId = get(businessInfo, 'id', '')
  const businessTitle = get(businessInfo, 'businessTitle')
  const accountId = searchParams.get('account_id')
  const invoiceId = searchParams.get('invoice_id')
  const statementId = searchParams.get('statement_id')
  const uiRef = useRef(null)
  const mutationRef = useRef()
  const canRegister = urlBusinessName && accountId
  // Held in a ref so the FirebaseUI sign-in callback (captured once by the
  // initializeUi useEffect) always reads the latest businessId after the
  // slug→UUID lookup resolves. Using component state here causes the callback
  // to lock onto the initial '' and POST register with businessId: null.
  const businessIdRef = useRef('')

  useEffect(() => {
    if (businessInfo?.id) {
      businessIdRef.current = businessInfo.id
    }
  }, [businessInfo])

  const goToHomePage = () => {
    if (invoiceId) {
      history.push(routes.app.payments)
    } else if (statementId) {
      history.push(routes.app.statements)
    } else {
      history.push(routes.app.home)
    }
  }

  const resetUI = () => {
    setShowRecoverPasswordBtn(false)
    setRecoverPasswordScreen({ isOpen: false, email: '' })
    setPasswordLinkingEmail('')
    if (uiRef.current) {
      uiRef.current.reset()
    }
    initializeUi()
  }

  // For invite-link flows (canRegister), defer initial UI mount until the
  // slug→UUID lookup has resolved — otherwise a fast submit POSTs register
  // with an empty businessId, which the backend rejects with
  // HttpMessageNotReadableException ("Missing request payload"). For non-
  // invite flows this is `true` from first render so the effect runs once,
  // matching the original behavior — the resetUI effect below owns re-init.
  const initReady = !canRegister || Boolean(businessInfo?.id)

  useEffect(() => {
    if (!initReady) return

    initializeUi()

    return () => {
      if (uiRef.current) {
        uiRef.current.reset()
        firebaseui.auth.AuthUI.getInstance()?.delete()
      }
    }
  }, [urlBusinessName, accountId, initReady])

  const handleAccountDeactivatedError = (logout = false) => {
    setHasAccountDeactivatedError(true)
    if (logout) handleFirebaseLogout()
    resetUI()
  }

  const handleAccountIdError = (logout = false) => {
    setHasAccountIdError(true)
    if (logout) handleFirebaseLogout()
    resetUI()
  }

  const handleRegisterError = error => {
    handleError(error)
    handleFirebaseLogout()
    resetUI()
  }

  const handleRegisterUser = authResult => {
    dispatch(
      setSelectedLoginDetails({
        accountId: '',
        businessName: '',
        businessId: businessIdRef.current,
        show: !accountId,
        redirectToInvoicePage: !!invoiceId,
      })
    )
    const user = get(authResult, 'user', {})
    const displayName = get(user, 'displayName', '')
    const additionalUserInfo = get(authResult, 'additionalUserInfo', {})
    const providerId = get(additionalUserInfo, 'providerId', '')
    const { firstName, lastName } = getFirstNameAndLastName(displayName, providerId, get(additionalUserInfo, 'profile', {}))
    const userIdpId = get(user, 'uid', '')
    const email = get(user, 'email', '')

    if (canRegister) {
      if (!businessIdRef.current) {
        // Defensive: the gate on initializeUi should keep us out of this
        // branch, but if the form ever ends up active without businessInfo,
        // don't POST an empty UUID — surface a retry-able error.
        handleRegisterError({ data: { message: 'Business info is still loading. Please try again.' } })
        return
      }
      // New user flow. accountVerified means the user reached here via the generic self-signup form
      // (account number + invoice number); otherwise they arrived from an account-specific email link.
      const registrationSource = accountVerified ? 'GENERIC_SELF_SIGNUP' : 'ACCOUNT_SPECIFIC_LINK'
      // The full landing URL the user registered from, so support can see the exact link they used.
      const registrationUrl = window.location.href
      // Who sent the invite, carried on the account-specific link (empty for generic self-signup).
      const invitedBy = searchParams.get('invited_by')
      const payload = { providerId, userIdpId, businessId: businessIdRef.current, accountId, email, firstName, lastName, registrationSource, registrationUrl, invitedBy }
      registerUser(payload)
        .unwrap()
        .then(() => {
          // Select newly created account
          dispatch(setSelectedLoginDetails({ accountId, businessName: urlBusinessName, businessId: businessIdRef.current }))

          // After successful registration, refetch token to get new scope
          auth.currentUser
            .getIdToken(true)
            .then(goToHomePage)
            .catch(error => console.log('Token error --', error))
        })
        .catch(error => {
          const errorMessage = get(error, 'data.message', '').toLowerCase()
          if (errorMessage.includes(`User with email: ${email} is already registered for accountId: ${accountId}`.toLowerCase())) {
            dispatch(setSelectedLoginDetails({ accountId, businessName: urlBusinessName, businessId }))
            goToHomePage()
            return
          }

          if (errorMessage.includes(`no active record exists for accountid: ${accountId}, businessid: ${businessId}`.toLowerCase())) {
            handleAccountDeactivatedError(true)
            return
          }

          handleRegisterError(error)
        })
      return
    }

    // Existing user flow
    const accessToken = get(authResult, 'user.accessToken', '')
    const tokenInfo = jwtDecode(accessToken)
    console.log('ACCESS TOKEN HERE', tokenInfo)
    const tenants = get(tokenInfo, 'tenants', [])
    if (tenants.length === 0) {
      handleAccountIdError(true)
      return
    }

    if (isSSOProvider(providerId)) {
      updateGCPUser({ idpId: providerId, email }).unwrap().then(goToHomePage).catch(handleRegisterError)
      return
    }

    goToHomePage()
  }

  const setupNewUiInstance = () => {
    const ui = new firebaseui.auth.AuthUI(auth)
    auth.tenantId = PORTAL_AUTH_TENANT_ID
    ui.disableAutoSignIn()

    uiRef.current = ui

    ui.start('#auth-container', {
      signInFlow: 'popup',
      signInSuccessUrl: routes.app.home,
      callbacks: {
        signInSuccessWithAuthResult: authResult => {
          handleRegisterUser(authResult)
          return false
        },
        signInFailure: error => console.log('Failure error --', error),
      },
      signInOptions,
      credentialHelper: firebaseui.auth.CredentialHelper.GOOGLE_YOLO,
    })
  }

  const initializeUi = () => {
    const existingUi = firebaseui.auth.AuthUI.getInstance()

    if (existingUi) {
      existingUi.delete().then(() => {
        setupNewUiInstance()
      })
    } else {
      setupNewUiInstance()
    }
  }

  useEffect(() => {
    if (currentBusinessName) {
      getBusinessInfo(currentBusinessName)
    }
  }, [currentBusinessName, getBusinessInfo])

  useEffect(() => {
    if (canRegister && businessId) {
      getAccountStatus({ businessId, accountId })
    }
  }, [canRegister, businessId])

  useEffect(() => {
    if (isGetAccountStatusSuccess) {
      // update callback to handle account status
      resetUI()
      return
    }

    if (!canRegister && isGetBusinessInfoSuccess) {
      // update callback to handle new business id
      resetUI()
    }
  }, [canRegister, isGetBusinessInfoSuccess, isGetAccountStatusSuccess])

  const handleRecoverPassword = () => {
    const emailEl = document.getElementById('ui-sign-in-email-input')
    setShowRecoverPasswordBtn(false)
    setRecoverPasswordScreen({ isOpen: true, email: passwordLinkingEmail || get(emailEl, 'value', '') })
  }

  const mutationCallback = useCallback(() => {
    const isIDPListScreen = document.getElementsByClassName('firebaseui-idp-list')?.length
    const customAuthContainer = document.getElementById('custom-auth-container')
    if (customAuthContainer) {
      customAuthContainer.style.display = isIDPListScreen ? 'block' : 'none'
    }

    const emailEl = document.getElementById('ui-sign-in-email-input')
    if (emailEl) {
      setHasAccountIdError(false)
      setHasAccountDeactivatedError(false)
    }

    // FirebaseUI renders this page only when the existing sign-in method is a password; a
    // collision with another provider gets firebaseui-id-page-federated-linking instead.
    const passwordLinkingPage = get(document.getElementsByClassName('firebaseui-id-page-password-linking'), [0])
    const passwordLinkingEmailEl = passwordLinkingPage && get(passwordLinkingPage.getElementsByTagName('strong'), [0])
    setPasswordLinkingEmail(get(passwordLinkingEmailEl, 'textContent', ''))

    const firstAndLastNameEl = document.getElementById('ui-sign-in-name-input')
    if (firstAndLastNameEl && !canRegister) {
      handleAccountIdError()
      return
    }

    if (firstAndLastNameEl && !isActiveAccount) {
      handleAccountDeactivatedError()
      return
    }

    const trobleSignInEl = get(document.getElementsByClassName('firebaseui-form-links'), [0])
    if (trobleSignInEl && !passwordLinkingPage) {
      setShowRecoverPasswordBtn(true)
      return
    }

    setShowRecoverPasswordBtn(false)
  }, [isActiveAccount])

  useMutationObserver(mutationRef, mutationCallback)

  return (
    <>
      {(isLoadingGetBusinessInfo || isLoadingRegisterUser || isLoadingUpdateUser) && <Loader />}
      <Card sx={{ m: 'auto', width: '352px', boxShadow: `0px 1px 8px 0px ${theme.palette.divider}` }}>
        <Box ref={mutationRef}>
          {accountVerified ? (
            <Box sx={{ position: 'relative', p: 0, marginTop: 2 }}>
              <Typography variant="body2" color="textSecondary" sx={{ mb: 3 }} align="center">
                What email or single sign in option would you like to use to create your account?
              </Typography>
            </Box>
          ) : null}
          <FacebookLogin
            auth={auth}
            onRegisterUser={handleRegisterUser}
            onResetPassword={email => setRecoverPasswordScreen({ isOpen: true, email })}
          />
          {passwordLinkingEmail && (
            <Alert
              severity="info"
              sx={{ mx: 3, mb: 2, alignItems: 'center' }}
              action={
                <Button color="inherit" size="small" onClick={handleRecoverPassword}>
                  {T.RESET_PASSWORD}
                </Button>
              }
            >
              <Typography variant="h6" fontWeight={400}>
                {T.EMAIL_HAS_PASSWORD_LOGIN}
              </Typography>
            </Alert>
          )}
          <Box sx={{ position: 'relative', p: 0 }}>
            {/* Container for auth UI */}
            <Box id="auth-container" />

            {/* Back button overlaid on top */}
            <Box
              sx={{
                position: 'relative',
                bottom: 0,
                left: 0,
                width: '100%',
                display: 'flex',
                justifyContent: 'end',
                zIndex: theme.zIndex.modal,
              }}
            >
              {accountVerified ? (
                <Button
                  variant="text"
                  onClick={onBack}
                  fullWidth
                  sx={{
                    mt: 2,
                    backgroundColor: theme.palette.background.paper,
                    '&:hover': {
                      backgroundColor: theme.palette.background.paper,
                    },
                  }}
                >
                  Go Back
                </Button>
              ) : !accountId ? (
                <Button
                  variant="text"
                  color="primary"
                  onClick={goToCreate}
                  sx={{
                    width: 'fit-content',
                    textAlign: 'right',
                    justifyContent: 'end',
                    mr: 2,
                    mb: 2,
                  }}
                >
                  Don&#39;t have an Account? <br />
                  Create one now!
                </Button>
              ) : null}
            </Box>
          </Box>

          {showRecoverPasswordBtn && (
            <Box position="relative">
              <Button
                onClick={handleRecoverPassword}
                sx={{ position: 'absolute', ml: 2, mb: 2, zIndex: theme.zIndex.tooltip, bottom: 24 }}
                color="primary"
                variant="text"
              >
                Trouble signing in?
              </Button>
            </Box>
          )}
          {recoverPasswordScreen.isOpen && (
            <RecoverPassword
              isOpen={recoverPasswordScreen.isOpen}
              existingEmail={recoverPasswordScreen.email}
              businessId={businessId}
              accountId={accountId}
              invoiceId={invoiceId}
              onClose={onBack}
            />
          )}
        </Box>
        {hasAccountIdError && (
          <Alert severity="error" sx={{ mx: 3, mb: 2, alignItems: 'center' }}>
            <Typography variant="h6" fontWeight={400}>
              {T.ACCOUNT_NUMBER_DOESNT_EXIST}
            </Typography>
          </Alert>
        )}
        {hasAccountDeactivatedError && (
          <Alert severity="error" sx={{ mx: 3, mb: 2, alignItems: 'center' }}>
            <Typography variant="h6" fontWeight={400}>
              Cannot connect to a deactivated account. Please contact your hauler for assistance.
            </Typography>
          </Alert>
        )}
      </Card>
    </>
  )
}

export default SSOLogin
